import { Checkbox, Form } from 'antd';
import Image from 'next/image';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AiOutlineCalendar,
  AiOutlineClockCircle,
  AiOutlineEnvironment,
  AiOutlineLock,
  AiOutlineMail,
  AiOutlineUser,
} from 'react-icons/ai';

import cloudinaryUrl from '@/utils/cloudinary';
import { CARD_NUMBER_PATTERN, CVC_PATTERN } from '@/utils/card';
import { getCurrencySymbol } from '@/utils/currency';
import { BookingCost, Location, Unit } from '@core/booking/booking.types';
import Dropdown from 'common/atoms/dropdown';
import Input from 'common/atoms/input';

import NoAvailablePayment from '../payment-info-form/NoAvailablePayment';

interface CheckoutStepProps {
  room: Unit | null;
  location: Location | null;
  bookingCost: BookingCost | null;
  startTime: string;
  endTime: string;
  dateLabel: string;
  durationLabel: string;
  personalInfo: {
    name: string;
    email: string;
  };
  // Payment form props
  form: any;
  paymentInfo: any;
  handlePaymentInfoInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaymentTypeChange: (value: string) => void;
  availablePaymentMethods: string[];
  hideZipAndAddress: boolean;
  onPay: () => void;
  loading: boolean;
  onApplyDiscountCode: (code: string) => void;
}

const CheckoutStep: FC<CheckoutStepProps> = ({
  room,
  location,
  bookingCost,
  startTime,
  endTime,
  dateLabel,
  durationLabel,
  personalInfo,
  form,
  paymentInfo,
  handlePaymentInfoInputChange,
  handlePaymentTypeChange,
  availablePaymentMethods,
  hideZipAndAddress,
  onPay,
  loading,
  onApplyDiscountCode,
}) => {
  const { t } = useTranslation();
  const [discountInput, setDiscountInput] = useState('');
  const [discountApplying, setDiscountApplying] = useState(false);
  const imageUrl = room?.image_link ? cloudinaryUrl(room.image_link, 500) : '/placeholder.webp';
  const hasCreditCard = availablePaymentMethods.find((method) => method === 'credit_card') !== undefined;

  if (!hasCreditCard) {
    return <NoAvailablePayment />;
  }

  const currencySymbol = getCurrencySymbol(bookingCost?.currency, location);
  const totalText = bookingCost ? `${currencySymbol}${bookingCost.total.toFixed(2)}` : '';

  return (
    <div className="flex flex-col md:flex-row gap-6 p-2 md:p-0">
      {/* Left Column — Order Summary */}
      <div
        className="w-full md:w-96 flex-shrink-0 rounded-2xl p-6"
        style={{ backgroundColor: '#F8F9FA', border: '1px solid #E5E7EB' }}
      >
        <h3 className="text-base font-bold m-0 mb-4" style={{ color: '#404D61' }}>
          {t('meetingRoom.yourBooking')}
        </h3>

        {/* Room Image */}
        <div className="relative w-full overflow-hidden rounded-xl mb-4" style={{ height: 160 }}>
          <Image
            src={imageUrl}
            alt={room?.name || t('meetingRoom.room')}
            fill
            style={{ objectFit: 'cover' }}
            unoptimized
          />
        </div>

        {/* Room Info */}
        <h4 className="text-lg font-bold m-0 mb-1" style={{ color: '#404D61' }}>
          {room?.name}
        </h4>
        <div className="flex items-center gap-1 mb-4" style={{ color: '#707070' }}>
          <AiOutlineEnvironment size={14} />
          <span className="text-sm">
            {location?.name}
            {location?.address ? ` · ${location.address}` : ''}
          </span>
        </div>

        {/* Booking Details */}
        <div
          className="space-y-2 mb-4 py-4"
          style={{ borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}
        >
          <div className="flex items-center gap-2">
            <AiOutlineCalendar size={16} style={{ color: 'var(--main)' }} />
            <span className="text-sm" style={{ color: '#404D61' }}>
              {dateLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <AiOutlineClockCircle size={16} style={{ color: 'var(--main)' }} />
            <span className="text-sm" style={{ color: '#404D61' }}>
              {startTime}
              {' – '}
              {endTime}
              {durationLabel ? ` (${durationLabel})` : ''}
            </span>
          </div>
          {personalInfo.name && (
            <div className="flex items-center gap-2">
              <AiOutlineUser size={16} style={{ color: 'var(--main)' }} />
              <span className="text-sm" style={{ color: '#404D61' }}>
                {personalInfo.name}
              </span>
            </div>
          )}
          {personalInfo.email && (
            <div className="flex items-center gap-2">
              <AiOutlineMail size={16} style={{ color: 'var(--main)' }} />
              <span className="text-sm" style={{ color: '#404D61' }}>
                {personalInfo.email}
              </span>
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        {bookingCost && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span style={{ color: '#707070' }}>{t('meetingRoom.summary.subtotal')}</span>
              <span
                className="font-medium"
                style={{ color: '#404D61' }}
                dangerouslySetInnerHTML={{ __html: `${currencySymbol}${bookingCost.subTotal.toFixed(2)}` }}
              />
            </div>
            {bookingCost.vat > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: '#707070' }}>{t('meetingRoom.summary.vat', { percent: bookingCost.vat })}</span>
                <span
                  className="font-medium"
                  style={{ color: '#707070' }}
                  dangerouslySetInnerHTML={{
                    __html: `${currencySymbol}${(bookingCost.total - bookingCost.subTotal).toFixed(2)}`,
                  }}
                />
              </div>
            )}

            {/* Discount Code */}
            <div className="mt-3 mb-3">
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#707070' }}>
                {t('meetingRoom.discount.label')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discountInput}
                  onChange={(e) => setDiscountInput(e.target.value)}
                  placeholder={t('payment.enterCode')}
                  className="flex-1 px-3 py-2 text-sm rounded-lg border outline-none"
                  style={{ borderColor: '#E5E7EB', color: '#404D61' }}
                />
                <button
                  type="button"
                  disabled={!discountInput.trim() || discountApplying}
                  onClick={() => {
                    setDiscountApplying(true);
                    onApplyDiscountCode(discountInput.trim());
                    setTimeout(() => setDiscountApplying(false), 500);
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium border-none cursor-pointer text-white"
                  style={{
                    backgroundColor: discountInput.trim() && !discountApplying ? 'var(--main)' : '#D1D5DB',
                  }}
                >
                  {discountApplying ? '...' : t('payment.applyCode')}
                </button>
              </div>
              {discountInput.trim() && bookingCost.discountCode !== undefined && (
                <p
                  className="text-xs mt-1.5 m-0 font-medium"
                  style={{ color: bookingCost.discountCode ? '#166534' : '#DC2626' }}
                >
                  {bookingCost.discountCode
                    ? t('payment.discountApplied', { code: bookingCost.discountCode })
                    : t('payment.codeInvalid')}
                </p>
              )}
            </div>

            <div
              className="flex justify-between text-lg font-bold mt-2 pt-2"
              style={{ borderTop: '1px solid #E5E7EB' }}
            >
              <span style={{ color: '#404D61' }}>{t('meetingRoom.summary.total')}</span>
              <span
                style={{ color: 'var(--main)' }}
                dangerouslySetInnerHTML={{ __html: `${currencySymbol}${bookingCost.total.toFixed(2)}` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Right Column — Payment Form */}
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-bold m-0 mb-4" style={{ color: '#404D61' }}>
          {t('meetingRoom.paymentDetails')}
        </h3>

        <Form form={form} layout="vertical" className="w-full">
          <Form.Item
            initialValue={paymentInfo.paymentType}
            name="paymentType"
            label={t('payment.paymentType')}
            rules={[{ required: true, message: t('payment.validation.paymentTypeRequired') }]}
            style={{ marginBottom: 16 }}
          >
            <Dropdown
              onChange={handlePaymentTypeChange}
              options={[{ value: 'credit_card', label: t('payment.creditCard') }]}
              name={t('payment.paymentType')}
            />
          </Form.Item>

          <Form.Item
            initialValue={paymentInfo.cardNumber}
            name="cardNumber"
            label={t('payment.cardNumber')}
            rules={[
              { required: true, message: t('payment.validation.cardNumberRequired') },
              {
                pattern: CARD_NUMBER_PATTERN,
                message: t('payment.validation.invalidCard'),
              },
            ]}
            style={{ marginBottom: 16 }}
          >
            <Input
              onChange={handlePaymentInfoInputChange}
              name="cardNumber"
              placeholder={t('payment.placeholders.cardNumber')}
            />
          </Form.Item>

          <Form.Item
            initialValue={paymentInfo.nameOnTheCard}
            name="nameOnTheCard"
            label={t('payment.nameOnCard')}
            rules={[
              { required: true, message: t('payment.validation.nameRequired') },
              { pattern: /^[\p{L}\p{M}\s'’.-]{2,30}$/u, message: t('payment.validation.nameInvalid') },
            ]}
            style={{ marginBottom: 16 }}
          >
            <Input onChange={handlePaymentInfoInputChange} name="nameOnTheCard" placeholder={t('payment.nameOnCard')} />
          </Form.Item>

          <div className="flex gap-4">
            <Form.Item label={t('payment.expirationDate')} required style={{ marginBottom: 16, flex: 1 }}>
              <div className="flex items-center gap-2" dir="ltr">
                <Form.Item
                  initialValue={paymentInfo.expireDateMonth}
                  name="expireDateMonth"
                  noStyle
                  rules={[
                    { required: true, message: t('payment.validation.monthRequired') },
                    { pattern: /^(0[1-9]|1[0-2])$/, message: t('payment.validation.month') },
                  ]}
                >
                  <Input
                    onChange={handlePaymentInfoInputChange}
                    name="expireDateMonth"
                    placeholder={t('payment.placeholders.mm')}
                  />
                </Form.Item>
                <span style={{ color: '#9CA3AF' }}>/</span>
                <Form.Item
                  initialValue={paymentInfo.expireDateYear}
                  name="expireDateYear"
                  noStyle
                  rules={[
                    { required: true, message: t('payment.validation.yearRequired') },
                    { pattern: /^(2[3-9]|3[0-9])$/, message: t('payment.validation.year') },
                  ]}
                >
                  <Input
                    onChange={handlePaymentInfoInputChange}
                    name="expireDateYear"
                    placeholder={t('payment.placeholders.yy')}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              initialValue={paymentInfo.cvv}
              name="cvc_cvv"
              label={t('payment.cvc')}
              rules={[
                { required: true, message: t('common.required') },
                { pattern: CVC_PATTERN, message: t('payment.validation.cvcInvalid') },
              ]}
              style={{ marginBottom: 16, width: 120 }}
            >
              <Input onChange={handlePaymentInfoInputChange} name="cvv" placeholder={t('payment.placeholders.cvc')} />
            </Form.Item>
          </div>

          {!hideZipAndAddress && (
            <Form.Item
              initialValue={paymentInfo.address}
              name="billingAddress"
              label={t('payment.billingAddress')}
              rules={[{ required: true, message: t('payment.validation.billingAddressRequired') }]}
              style={{ marginBottom: 16 }}
            >
              <Input onChange={handlePaymentInfoInputChange} name="address" placeholder={t('payment.billingAddress')} />
            </Form.Item>
          )}

          {!hideZipAndAddress && (
            <Form.Item
              initialValue={paymentInfo.zip}
              name="ZIP"
              label={t('payment.zipCode')}
              rules={[
                { required: true, message: t('payment.validation.zipRequired') },
                { pattern: /^\d{5}$/, message: t('payment.validation.zipInvalid') },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input onChange={handlePaymentInfoInputChange} name="zip" placeholder={t('payment.placeholders.zip')} />
            </Form.Item>
          )}
        </Form>

        {/* Save CC Checkbox */}
        <Checkbox
          checked={paymentInfo.save_cc}
          onChange={(e) =>
            handlePaymentInfoInputChange({ target: { name: 'save_cc', value: e.target.checked } } as any)
          }
          style={{ marginBottom: 16 }}
        >
          <span className="text-sm" style={{ color: '#404D61' }}>
            {t('payment.saveCard')}
          </span>
        </Checkbox>

        {/* Security Badge */}
        <div className="flex items-center gap-2 mb-6 mt-2">
          <AiOutlineLock size={16} style={{ color: 'var(--main)' }} />
          <span className="text-sm font-medium" style={{ color: 'var(--main)' }}>
            {t('payment.tlsNotice')}
          </span>
        </div>

        {/* Pay Button */}
        <button
          type="button"
          onClick={onPay}
          disabled={loading}
          className="w-full py-4 rounded-xl text-white text-base font-semibold border-none cursor-pointer transition-opacity"
          style={{
            backgroundColor: 'var(--main)',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? t('payment.processing') : t('payment.payAmount', { amount: totalText })}
        </button>
      </div>
    </div>
  );
};

export default CheckoutStep;
